import React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface FullPageStateProps {
  /** Large mono marker above the title, e.g. "404". */
  code?: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

/** Centred full-viewport message used for 404s, crashes and similar dead ends. */
export function FullPageState({
  code,
  title,
  description,
  icon,
  actions,
  children,
  className,
}: FullPageStateProps) {
  return (
    <div
      className={cn(
        "flex min-h-screen items-center justify-center bg-background px-4 py-16 text-foreground",
        className,
      )}
    >
      <div className="w-full max-w-md text-center">
        {icon && (
          <div className="mx-auto mb-6 flex size-11 items-center justify-center rounded-md border border-border bg-card text-muted-foreground [&_svg]:size-5">
            {icon}
          </div>
        )}
        {code && <p className="mb-3 font-mono text-sm tracking-widest text-muted-foreground">{code}</p>}
        <h1 className="text-display-sm text-foreground">{title}</h1>
        {description && <p className="mt-2 text-sm leading-6 text-body">{description}</p>}
        {children}
        {actions && <div className="mt-8 flex flex-wrap justify-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

/** Minimal boot/loading screen. */
export function FullPageLoader({ label = "Loading" }: { label?: string }) {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        <span className="font-mono text-xs tracking-wider uppercase">{label}</span>
      </div>
    </div>
  );
}
